import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { LuExternalLink, LuListChecks, LuLoader2 } from 'react-icons/lu';
import { Input, SegmentedControl } from 'components/common';
import { getPifCheckContracts } from 'redux/crm/project/contractPifCheckThunks';
import PifCheckContractList from 'components/app/crm/pif-check/PifCheckContractList';
import PifCheckTab from 'components/app/crm/pif-check/PifCheckTab';

const PER_PAGE = 30;
const SEARCH_DELAY_MS = 350;
const STATUS_OPTIONS = [
  { value: 'unchecked', label: 'To check' },
  { value: 'checked', label: 'Checked' },
  { value: 'no_proposal', label: 'Proposal missing' },
  { value: 'no_contract', label: 'No contract' },
  { value: 'all', label: 'All' },
];

// Commercial → PIF Check: pick a contract, then compare the Proposal Sales sent
// back with the PIF of a chosen CO#, building by building. The selected
// contract lives in the URL (?contract=) so a check can be linked to.
const PifCheckPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('contract')) || null;
  const selectedProjectId = Number(searchParams.get('project')) || null;

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [status, setStatus] = useState('unchecked');
  const [list, setList] = useState({ rows: null, total: 0, page: 1, noContractTotal: 0 });
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState('');
  const requestRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const fetchPage = useCallback(async (page) => {
    const request = ++requestRef.current;
    if (page === 1) setFetching(true);
    try {
      const res = await dispatch(getPifCheckContracts({ q: debouncedQuery, status, page, per_page: PER_PAGE })).unwrap();
      if (request !== requestRef.current) return; // a newer search replaced this one
      setList((prev) => ({
        rows: page === 1 ? res.rows : [...(prev.rows || []), ...res.rows],
        total: res.total,
        page,
        noContractTotal: res.no_contract_total || 0,
      }));
      setError('');
    } catch (e) {
      if (request === requestRef.current) {
        setList((prev) => ({ ...prev, rows: prev.rows || [] }));
        setError(typeof e === 'string' ? e : 'Could not load contracts.');
      }
    } finally {
      if (request === requestRef.current) setFetching(false);
    }
  }, [dispatch, debouncedQuery, status]);
  // A search or tab change is running: typed but not sent yet (debounce), or sent
  // and waiting — the old list stays, dimmed, with "Searching…" above it.
  const searching = fetching || query.trim() !== debouncedQuery;

  useEffect(() => { fetchPage(1); }, [fetchPage]);

  // The selected contract's row: from the list when it is there, otherwise
  // fetched by id (deep link, or filtered out of the current list).
  const refreshSelected = useCallback(async () => {
    if (!selectedId) { setSelected(null); return; }
    try {
      const res = await dispatch(getPifCheckContracts({ contract_id: selectedId })).unwrap();
      const row = res.rows[0] || null;
      setSelected(row);
      // The list may not have answered yet (deep link): leave its state alone.
      if (row) setList((prev) => (prev.rows ? {
        ...prev, rows: prev.rows.map((r) => (r.contract_id === row.contract_id ? row : r)),
      } : prev));
      if (!row) setError('That contract is not in your scope, or no longer exists.');
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Could not open that contract.');
    }
  }, [dispatch, selectedId]);

  useEffect(() => { refreshSelected(); }, [refreshSelected]);

  // Same, for a project without a contract (?project=).
  const refreshSelectedProject = useCallback(async () => {
    if (!selectedProjectId) { setSelectedProject(null); return; }
    try {
      const res = await dispatch(getPifCheckContracts({ status: 'no_contract', project_id: selectedProjectId })).unwrap();
      const row = res.rows[0] || null;
      setSelectedProject(row);
      if (row) setList((prev) => (prev.rows ? {
        ...prev, rows: prev.rows.map((r) => (r.no_contract && r.project_id === row.project_id ? row : r)),
      } : prev));
      if (!row) setError('That project is not in your scope, or it has a contract now — look for the contract.');
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Could not open that project.');
    }
  }, [dispatch, selectedProjectId]);

  useEffect(() => { refreshSelectedProject(); }, [refreshSelectedProject]);

  const select = (row) => {
    if (row.no_contract) {
      setSelectedProject(row);
      setSelected(null);
      setSearchParams({ project: String(row.project_id) });
      return;
    }
    setSelectedProject(null);
    setSelected(row);
    setSearchParams({ contract: String(row.contract_id) });
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try { await fetchPage(list.page + 1); } finally { setLoadingMore(false); }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <div className="tw-flex tw-items-center tw-gap-3">
        <LuListChecks className="tw-text-primary-600" size={22} aria-hidden="true" />
        <div>
          <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">PIF Check</h1>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0">
            Compare the Proposal Sales sent back with the PIF of a CO#: building parameters and design loads, building by building.
          </p>
        </div>
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[340px_minmax(0,1fr)] tw-gap-3 tw-items-start">
        <aside className="tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-p-3 tw-flex tw-flex-col tw-gap-3">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Contract no., project, J#, Q#…"
            aria-label="Search contracts"
          />
          <SegmentedControl size="sm" className="tw-flex-wrap" value={status} onChange={setStatus} ariaLabel="Check status" options={STATUS_OPTIONS} />
          {status === 'no_proposal' && (
            <p className="tw-m-0 tw-text-xs tw-text-slate-500">
              Contracts with neither a Proposal file nor typed values yet — nothing to compare with the PIF.
              Open one and upload the Proposal, or type its values (step 2).
            </p>
          )}
          {status === 'no_contract' && (
            <p className="tw-m-0 tw-text-xs tw-text-slate-500">
              Projects with a job number but no contract yet — check them here. Searching also finds the ones with no job number and Lost ones.
            </p>
          )}
          {searching && list.rows && (
            <p role="status" className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-primary-600">
              <LuLoader2 size={13} className="tw-animate-spin" aria-hidden="true" /> Searching…
            </p>
          )}
          <PifCheckContractList
            rows={list.rows}
            busy={searching}
            total={list.total}
            selectedId={selectedId}
            selectedProjectId={selectedProjectId}
            emptyText={status === 'no_contract' ? 'No projects match.' : undefined}
            loading={loadingMore}
            onSelect={select}
            onLoadMore={loadMore}
          />
          {!searching && status !== 'no_contract' && list.noContractTotal > 0 && (
            <button type="button" onClick={() => setStatus('no_contract')}
              className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-left tw-text-xs tw-text-primary-600 tw-cursor-pointer">
              {list.noContractTotal === 1
                ? '1 project without a contract matches'
                : `${list.noContractTotal} projects without a contract match`}
            </button>
          )}
        </aside>

        <main className="tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-p-4 tw-min-h-[240px] tw-flex tw-flex-col tw-gap-3">
          {error && <p className="tw-m-0 tw-text-sm tw-text-red-600">{error}</p>}
          {selectedProject && !selected && (
            <>
              <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
                <h2 className="tw-m-0 tw-text-base tw-font-bold tw-text-slate-800">{selectedProject.project_name}</h2>
                <Link to={`/crm/projects/${selectedProject.project_id}`} target="_blank" rel="noopener noreferrer"
                  className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-primary-600">
                  Open project <LuExternalLink size={12} />
                </Link>
              </div>
              <p className="tw-m-0 tw-text-xs tw-text-slate-500">
                No contract yet. This check and its Proposal file: everything moves to the contract when one is created for these buildings.
              </p>
              <PifCheckTab
                key={`project-${selectedProject.project_id}`}
                project={{ id: selectedProject.project_id }}
                onChanged={refreshSelectedProject}
              />
            </>
          )}
          {!selectedProject && (selected ? (
            <>
              <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
                <h2 className="tw-m-0 tw-text-base tw-font-bold tw-text-slate-800">{selected.contract_number || `Contract #${selected.contract_id}`}</h2>
                <span className="tw-text-sm tw-text-slate-500">{selected.project_name} · {selected.job_building_name}</span>
                {/* New tab, like View PIF: the check in progress stays open here. */}
                <Link to={`/crm/projects/${selected.project_id}`} target="_blank" rel="noopener noreferrer"
                  className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-primary-600">
                  Open project <LuExternalLink size={12} />
                </Link>
              </div>
              <PifCheckTab
                key={selected.contract_id}
                project={{ id: selected.project_id }}
                jobBuilding={{ id: selected.job_building_id }}
                contract={{ id: selected.contract_id }}
                onChanged={refreshSelected}
              />
            </>
          ) : (
            <p className="tw-m-auto tw-text-[13px] tw-text-slate-500 tw-text-center">
              Pick a contract on the left to compare its Proposal with the PIF.
            </p>
          ))}
        </main>
      </div>
    </div>
  );
};

export default PifCheckPage;
